import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Chip,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Article as ArticleIcon,
  Campaign as CampaignIcon,
  Share as ShareIcon,
  ShowChart as ShowChartIcon,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

type Period = '7d' | '30d' | '90d' | 'all';

interface TopCampaign {
  id: string;
  name: string;
  type: string;
  status: string;
}

interface PerformanceData {
  period: string;
  start_date: string;
  end_date: string;
  campaigns_created: number;
  leads_created: number;
  content_generated: number;
  posts_published: number;
  top_campaigns: TopCampaign[];
  lead_conversion_rate: number;
  content_per_campaign: number;
}

const PERIOD_LABELS: Record<Period, string> = {
  '7d': '7 Days',
  '30d': '30 Days',
  '90d': '90 Days',
  all: 'All Time',
};

const STATUS_COLOR: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error'> = {
  active: 'success',
  draft: 'default',
  paused: 'warning',
  completed: 'primary',
};

interface KpiCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color?: string;
  subtitle?: string;
}

function KpiCard({ title, value, icon, color = 'primary.main', subtitle }: KpiCardProps) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" sx={{ color, fontWeight: 700 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{ color, opacity: 0.7, ml: 1 }}>{icon}</Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function Performance() {
  const [period, setPeriod] = useState<Period>('30d');
  const [data, setData] = useState<PerformanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  const loadPerformance = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get<PerformanceData>(
        `${API_URL}/analytics/performance`,
        {
          params: { period },
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
        }
      );
      setData(response.data);
      setLastUpdate(new Date());
    } catch (err: any) {
      setError(
        err.response?.data?.detail ||
          err.message ||
          'Failed to load performance data'
      );
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    loadPerformance();
  }, [loadPerformance]);

  const handlePeriodChange = (_: React.MouseEvent<HTMLElement>, value: Period | null) => {
    if (value) setPeriod(value);
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 0 } }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Performance Overview
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Last updated: {lastUpdate.toLocaleTimeString()}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup
            value={period}
            exclusive
            onChange={handlePeriodChange}
            size="small"
            aria-label="time period"
          >
            {(Object.keys(PERIOD_LABELS) as Period[]).map((p) => (
              <ToggleButton key={p} value={p} aria-label={PERIOD_LABELS[p]}>
                {PERIOD_LABELS[p]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Tooltip title="Refresh">
            <IconButton onClick={loadPerformance} color="primary" size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Date range caption */}
      {data && !loading && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Showing data from <strong>{formatDate(data.start_date)}</strong> to{' '}
          <strong>{formatDate(data.end_date)}</strong>
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '40vh',
          }}
        >
          <CircularProgress />
        </Box>
      ) : data ? (
        <Grid container spacing={3}>
          {/* KPI Cards */}
          <Grid item xs={6} sm={4} md={2}>
            <KpiCard
              title="Campaigns Created"
              value={data.campaigns_created}
              icon={<CampaignIcon fontSize="large" />}
              color="primary.main"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <KpiCard
              title="Leads Created"
              value={data.leads_created}
              icon={<PeopleIcon fontSize="large" />}
              color="success.main"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <KpiCard
              title="Content Generated"
              value={data.content_generated}
              icon={<ArticleIcon fontSize="large" />}
              color="info.main"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <KpiCard
              title="Posts Published"
              value={data.posts_published}
              icon={<ShareIcon fontSize="large" />}
              color="secondary.main"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <KpiCard
              title="Lead Conversion Rate"
              value={`${data.lead_conversion_rate}%`}
              icon={<TrendingUpIcon fontSize="large" />}
              color={data.lead_conversion_rate >= 10 ? 'success.main' : 'warning.main'}
              subtitle="Leads per campaign"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <KpiCard
              title="Content per Campaign"
              value={data.content_per_campaign}
              icon={<ShowChartIcon fontSize="large" />}
              color="primary.main"
              subtitle="Avg pieces"
            />
          </Grid>

          {/* Top Campaigns */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                <CampaignIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Top Campaigns
                </Typography>
                <Chip
                  label={`${data.top_campaigns.length} campaign${data.top_campaigns.length !== 1 ? 's' : ''}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>
              <Divider sx={{ mb: 2 }} />
              {data.top_campaigns.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">
                    No campaigns found for this period.
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small" aria-label="top campaigns">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <Typography variant="caption" fontWeight={700} color="text.secondary">
                            CAMPAIGN NAME
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" fontWeight={700} color="text.secondary">
                            TYPE
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" fontWeight={700} color="text.secondary">
                            STATUS
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.top_campaigns.map((campaign) => (
                        <TableRow key={campaign.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {campaign.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ textTransform: 'capitalize' }}
                            >
                              {campaign.type}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={campaign.status}
                              size="small"
                              color={STATUS_COLOR[campaign.status] ?? 'default'}
                              sx={{ textTransform: 'capitalize' }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>
        </Grid>
      ) : null}
    </Box>
  );
}
